import type { ChangeEventHandler } from "react";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import type { PackagingPreference } from "@jti/fe-common/types";
import { ConsumablesFormStepSection } from "@jti/ui/components/ConsumablesFormStepSection";
import { QuantityRadioGroup } from "@jti/ui/components/QuantityRadioGroup";

import type { SticksFormValues } from "../../SticksForm.types";

import { QuantityGroupTitle } from "./components/QuantityGroupTitle";

import styles from "../../SticksForm.module.scss";

const QUANTITY_GROUP_NAME: keyof SticksFormValues = "quantity";
const MIN_ALLOWED_QUANTITY = 1;

type QuantityGroupProps = {
  step: number;
  onChange?: ChangeEventHandler<HTMLInputElement>;
};

const QUANTITY_BY_PACKAGING_PREFERENCE_DICT: Record<PackagingPreference, number> = {
  single_packs: 5,
  cartons: 3,
  mix_n_match: 3,
};

export const QuantityGroup = ({ step, onChange }: QuantityGroupProps) => {
  const { watch, register, setValue } = useFormContext<SticksFormValues>();

  const packagingPreference = watch("packagingPreference");
  const quantity = Number.parseInt(watch("quantity"));

  useEffect(() => {
    if (quantity > QUANTITY_BY_PACKAGING_PREFERENCE_DICT[packagingPreference]) {
      setValue("quantity", MIN_ALLOWED_QUANTITY.toString(), { shouldValidate: true });
    }
  }, [packagingPreference, register, quantity]);

  const registerProps = register(QUANTITY_GROUP_NAME, { onChange });

  return (
    <ConsumablesFormStepSection
      title={<QuantityGroupTitle step={step} />}
      className={styles.stepSection}
      data-testid="quantity-selection-step"
    >
      <QuantityRadioGroup
        registerProps={registerProps}
        groupName={QUANTITY_GROUP_NAME}
        numberOfElements={QUANTITY_BY_PACKAGING_PREFERENCE_DICT[packagingPreference]}
        type={packagingPreference === "single_packs" ? "packs" : "cartons"}
      />
    </ConsumablesFormStepSection>
  );
};
